/**
 * EAS Parser Service
 *
 * Parses Equipment Assessment Sheet (EAS) Excel files for ROI calculation.
 *
 * DATA SOURCE:
 * - Excel files (.xlsx/.xls) uploaded by users
 * - Standard format: Equipment Type, Tonnage, Year Manufactured, Operating Hours, Electricity Rate
 * - May include optional fields: Location, Manufacturer, Model, Refrigerant Type
 *
 * KEY ASSUMPTIONS:
 * - Files use standard Excel format (parsed client-side via xlsx library)
 * - Required fields: Equipment Type, Tonnage, Year Manufactured, Operating Hours, Electricity Rate
 * - Equipment types must map to known types: Chiller, RTU, CRAC, Split
 * - Numeric fields (tonnage, year, hours, rate) must be parseable as numbers
 *
 * EDGE CASES HANDLED:
 * - Formulas in cells (automatically evaluated by xlsx library)
 * - Text in numeric fields (flagged as validation errors)
 * - Unknown equipment types (mapped to closest match or flagged)
 * - Missing optional fields (allowed, returned as null)
 * - Header row variations (flexible matching)
 */

import * as XLSX from 'xlsx';

// ============================================================================
// CONSTANTS - Column Mappings and Equipment Types
// ============================================================================

/**
 * Standard column headers we expect in EAS files
 * Case-insensitive matching with flexible patterns
 */
export const EAS_COLUMN_PATTERNS = {
  equipment_id: /equipment\s*(id|#|num|number)|^id$/i,
  equipment_type: /dx.*ch.*ahu|"?dx"?\s*(or|,)\s*"?ch"?|equipment\s*type|^type$/i,  // Matches "DX, CH or AHU", "DX" or "CH", or "Equipment Type"
  rtu_or_split: /if.*dx.*rtu.*split|rtu\s*or\s*split/i,  // "If it's a DX unit, is it an RTU or Split System"
  location: /location|desription|description|site|building|area/i,  // Fixed typo in real file
  units: /^number\s*of\s*units$|^qty$|^quantity$/i,  // STRICT: Only match exact "Number of Units" header, not "Hours on Unit"
  manufacturer: /manufacturer|mfr|make|brand/i,
  model: /model(\s*(#|num|number))?/i,
  year_manufactured: /year\s*mfg|year\s*(manufactured|mfg\.?)|install|built|vintage|age/i,
  tonnage: /^tonnage$|^tons?$|^ton\s*size$|^capacity$|^size$/i,  // STRICT: Match standalone tonnage headers, not "Chiller Oil Capacity"
  refrigerant: /refrig|refrigerant(\s*type)?|ref\s*type/i,
  operating_hours: /hours.*year.*runs|cooling.*hours.*year|estimated.*hours.*year|operating\s*hours|hours(\s*per)?\s*(year|yr)|hrs?\/yr|est.*total.*run.*time.*hours|run\s*hours|annual\s*hours/i,
  electricity_rate: /cost\s*per\s*kwh|electricity.*rate|electric.*cost|kwh.*rate|rate.*kwh|\$\s*\/\s*kwh|kwh\s*cost|energy\s*cost/i
};

/**
 * Equipment type mappings - handles common variations
 * Maps user input to standardized types used in calculations
 */
export const EQUIPMENT_TYPE_MAP = {
  // Standard types
  'chiller': 'Chiller',
  'chillers': 'Chiller',
  'ch': 'Chiller',
  'water cooled': 'Chiller',
  'water-cooled': 'Chiller',
  'air cooled': 'Chiller',
  'air-cooled': 'Chiller',
  'centrifugal': 'Chiller',
  'screw chiller': 'Chiller',

  'rtu': 'RTU',
  'rtus': 'RTU',
  'rooftop': 'RTU',
  'rooftop unit': 'RTU',
  'ahu': 'RTU',  // Air Handling Units often similar to RTUs
  'air handler': 'RTU',
  'air handling': 'RTU',
  'air handling unit': 'RTU',
  'package': 'RTU',
  'packaged': 'RTU',
  'pkg': 'RTU',
  'package unit': 'RTU',
  'packaged unit': 'RTU',
  'fcu': 'RTU',
  'fan coil': 'RTU',
  'fancoil': 'RTU',
  'fan coil unit': 'RTU',

  'crac': 'CRAC',
  'cracs': 'CRAC',
  'computer room ac': 'CRAC',
  'computer room': 'CRAC',
  'precision cooling': 'CRAC',
  'data center': 'CRAC',

  'split': 'Split',
  'splits': 'Split',
  'dx': 'Split',  // DX systems are typically split systems
  'split system': 'Split',
  'ductless': 'Split',
  'mini-split': 'Split',
  'mini split': 'Split',
  'minisplit': 'Split',
  'ductless mini': 'Split',
  'vrf': 'Split',
  'vrv': 'Split',
  'variable refrigerant': 'Split',
  'variable refrigerant flow': 'Split',
  'heat pump': 'Split',
  'hp': 'Split',
  'wshp': 'Split',      // Water source heat pump
  'gshp': 'Split',      // Ground source heat pump
  'ashp': 'Split',      // Air source heat pump
  'ptac': 'Split',      // Package terminal AC
  'pthp': 'Split',      // Package terminal heat pump
  'window unit': 'Split',
  'wall unit': 'Split'
};

/**
 * Required fields for ROI calculation
 * All must be present and valid
 * Note: Tonnage moved to optional - if missing, estimated from equipment type
 */
export const REQUIRED_FIELDS = [
  'equipment_type',
  'year_manufactured',
  'operating_hours',
  'electricity_rate'
];

/**
 * Default tonnage estimates by equipment type
 * Used when tonnage column is missing from uploaded file
 */
export const DEFAULT_TONNAGE = {
  'Chiller': 150,   // Large commercial chiller
  'RTU': 25,        // Typical rooftop unit (upper range)
  'CRAC': 10,       // Computer room AC
  'Split': 5,       // Residential/light commercial (upper range)
  'Unknown': 5      // Conservative default
};

/**
 * Placeholder patterns - values that indicate missing/unknown data
 * These trigger specific warnings instead of generic null handling
 */
const PLACEHOLDER_PATTERNS = /^(x|n\/?a|tbd|tba|tk|-|—|unknown|\?|none|pending)$/i;

/**
 * Optional fields (nice to have but not required)
 */
export const OPTIONAL_FIELDS = [
  'equipment_id',
  'location',
  'units',
  'manufacturer',
  'model',
  'refrigerant'
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Detect if a value is a placeholder (X, N/A, TBD, etc.)
 *
 * @param {any} value - Cell value to check
 * @returns {boolean} True if value is a placeholder
 */
function detectPlaceholder(value) {
  if (value === undefined || value === null || value === '') return false;
  return PLACEHOLDER_PATTERNS.test(String(value).trim());
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Normalize equipment type to standard format
 *
 * @param {string} rawType - Raw equipment type from Excel (e.g., "CH", "chiller", "Split System")
 * @returns {string} Standardized type (Chiller, RTU, CRAC, Split) or "Unknown"
 */
export function normalizeEquipmentType(rawType) {
  if (!rawType) return 'Unknown';

  const normalized = String(rawType).toLowerCase().trim();

  // Direct lookup first
  if (EQUIPMENT_TYPE_MAP[normalized]) {
    return EQUIPMENT_TYPE_MAP[normalized];
  }

  // Fuzzy matching for partial matches
  for (const [key, value] of Object.entries(EQUIPMENT_TYPE_MAP)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return value;
    }
  }

  return 'Unknown';
}

/**
 * Calculate equipment age from manufacture year
 *
 * @param {number} yearManufactured - Year equipment was manufactured
 * @returns {number|null} Age in years, or null if invalid
 */
export function calculateAge(yearManufactured) {
  const year = parseInt(yearManufactured, 10);
  const currentYear = new Date().getFullYear();

  // Sanity checks
  if (isNaN(year) || year < 1900 || year > currentYear + 1) {
    return null;
  }

  return currentYear - year;
}

/**
 * Validate a single equipment record
 *
 * @param {Object} equipment - Parsed equipment object
 * @param {number} rowNum - Row number in Excel (for error messages)
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[] }
 */
export function validateEquipment(equipment, rowNum) {
  const errors = [];
  const warnings = [];

  // Check required fields
  if (!equipment.equipment_type || equipment.equipment_type === 'Unknown') {
    errors.push(`Row ${rowNum}: Equipment type is missing or invalid`);
  }

  // Tonnage is now optional - warn but don't error if missing (will use estimate)
  if (!equipment.tonnage || isNaN(equipment.tonnage) || equipment.tonnage <= 0) {
    const estimatedTonnage = DEFAULT_TONNAGE[equipment.equipment_type] || DEFAULT_TONNAGE['Unknown'];
    warnings.push(`Row ${rowNum}: Tonnage not specified, will use estimate (${estimatedTonnage} tons)`);
  }

  if (!equipment.year_manufactured) {
    errors.push(`Row ${rowNum}: Year manufactured is required`);
  } else {
    const age = calculateAge(equipment.year_manufactured);
    if (age === null) {
      errors.push(`Row ${rowNum}: Year manufactured (${equipment.year_manufactured}) is invalid`);
    } else if (age > 50) {
      warnings.push(`Row ${rowNum}: Equipment is ${age} years old (very old, consider replacement)`);
    } else if (age < 0) {
      errors.push(`Row ${rowNum}: Year manufactured is in the future`);
    }
  }

  if (!equipment.operating_hours || isNaN(equipment.operating_hours) || equipment.operating_hours <= 0) {
    errors.push(`Row ${rowNum}: Operating hours per year must be a positive number`);
  } else if (equipment.operating_hours > 8760) {
    errors.push(`Row ${rowNum}: Operating hours cannot exceed 8760 (hours in a year)`);
  }

  if (!equipment.electricity_rate || isNaN(equipment.electricity_rate) || equipment.electricity_rate <= 0) {
    errors.push(`Row ${rowNum}: Electricity rate must be a positive number`);
  }

  // Warnings for missing optional fields
  if (!equipment.units || equipment.units === 0) {
    warnings.push(`Row ${rowNum}: Number of units not specified, assuming 1`);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Validate entire EAS dataset
 *
 * @param {Array} equipment - Array of parsed equipment objects
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[], summary: Object }
 */
export function validateEAS(equipment) {
  const allErrors = [];
  const allWarnings = [];
  let validCount = 0;

  equipment.forEach((eq, idx) => {
    const rowNum = idx + 1;  // 1-indexed for user display
    const validation = validateEquipment(eq, rowNum);

    if (validation.valid) {
      validCount++;
    }

    allErrors.push(...validation.errors);
    allWarnings.push(...validation.warnings);
  });

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
    summary: {
      total: equipment.length,
      valid: validCount,
      invalid: equipment.length - validCount,
      completeness: Math.round((validCount / equipment.length) * 100)
    }
  };
}

// ============================================================================
// PARSING FUNCTIONS
// ============================================================================

/**
 * Find header row in Excel data
 * Looks for row containing column headers (fuzzy matching)
 *
 * @param {Array} rows - Array of arrays from Excel sheet
 * @returns {number} Index of header row, or -1 if not found
 */
function findHeaderRow(rows) {
  console.log('[EAS Parser] Searching for header row in first 15 rows...');

  for (let i = 0; i < Math.min(15, rows.length); i++) {
    const row = rows[i];
    if (!row) continue;

    // Log each row we're checking
    const rowPreview = row.slice(0, 5).map(c => String(c || '').substring(0, 20)).join(' | ');
    console.log(`[EAS Parser] Row ${i}: ${rowPreview}...`);

    // Count how many SEPARATE columns match different patterns
    // This prevents false positives from merged cells with descriptions like
    // "Required fields: Equipment Type, Tonnage, Year Manufactured..."
    const matchedColumnIndices = new Set();
    const matchedPatterns = new Set();

    // Helper to check if a cell is a valid header (short, not a sentence)
    const isValidHeaderCell = (cell) => {
      if (!cell) return false;
      const str = String(cell).trim();
      // Headers should be relatively short (under 60 chars) and not sentences
      return str.length > 0 && str.length < 60 && !str.includes(':');
    };

    row.forEach((cell, colIdx) => {
      if (!isValidHeaderCell(cell)) return;
      const cellStr = String(cell);

      // Check each pattern - only count if different column and valid header length
      for (const [fieldName, pattern] of Object.entries(EAS_COLUMN_PATTERNS)) {
        if (pattern.test(cellStr) && !matchedPatterns.has(fieldName)) {
          matchedColumnIndices.add(colIdx);
          matchedPatterns.add(fieldName);
          break;  // One pattern per cell
        }
      }
    });

    // Check for specific required patterns
    const hasEquipmentType = matchedPatterns.has('equipment_type');
    const hasTonnage = matchedPatterns.has('tonnage');
    const hasYearMfg = matchedPatterns.has('year_manufactured');
    const hasElecRate = matchedPatterns.has('electricity_rate');
    const hasOperatingHours = matchedPatterns.has('operating_hours');

    // Valid header row needs:
    // 1. Equipment type pattern
    // 2. At least 2 other recognized patterns (not all in same cell)
    // 3. Patterns must be in at least 3 different columns
    const hasEnoughPatterns = matchedPatterns.size >= 3;
    const hasEnoughColumns = matchedColumnIndices.size >= 3;
    const hasRequiredFields = hasEquipmentType && (hasTonnage || hasYearMfg || hasElecRate || hasOperatingHours);

    if (hasRequiredFields && hasEnoughPatterns && hasEnoughColumns) {
      console.log(`[EAS Parser] ✓ Found header row at index ${i} (matched ${matchedPatterns.size} patterns in ${matchedColumnIndices.size} columns)`);
      return i;
    }
  }

  console.error('[EAS Parser] ✗ Header row not found. Expected columns matching: Equipment Type + at least 2 other patterns in separate columns');
  return -1;  // Header not found
}

/**
 * Map column headers to our standard field names
 *
 * @param {Array} headerRow - Array of column header strings
 * @returns {Object} Map of column index to field name
 */
function mapColumns(headerRow) {
  const columnMap = {};
  const assignedFields = new Set();  // Track assigned fields to avoid duplicates
  const unmatchedHeaders = [];

  console.log('[EAS Parser] Mapping columns from header row...');

  headerRow.forEach((header, idx) => {
    if (!header) return;

    const headerStr = String(header).trim();
    let matched = false;

    // Try to match against known patterns
    for (const [fieldName, pattern] of Object.entries(EAS_COLUMN_PATTERNS)) {
      if (pattern.test(headerStr)) {
        // Skip if this field was already assigned (prefer first occurrence)
        if (assignedFields.has(fieldName)) {
          continue;
        }

        columnMap[idx] = fieldName;
        assignedFields.add(fieldName);
        matched = true;
        console.log(`[EAS Parser]   Column ${idx}: "${headerStr}" → ${fieldName}`);
        break;
      }
    }

    if (!matched && headerStr) {
      unmatchedHeaders.push(`"${headerStr}"`);
    }
  });

  // Log summary
  console.log(`[EAS Parser] Mapped ${Object.keys(columnMap).length} columns`);
  if (unmatchedHeaders.length > 0) {
    console.log(`[EAS Parser] Unrecognized headers: ${unmatchedHeaders.join(', ')}`);
  }

  return columnMap;
}

/**
 * Parse electricity rate from various formats
 * Handles: "0.14", "$0.14", "10-Cents", "10 cents", "14 cents/kWh", "10¢"
 *
 * @param {any} cellValue - Raw cell value
 * @returns {number|null} Parsed rate in $/kWh
 */
function parseElectricityRate(cellValue) {
  if (cellValue === undefined || cellValue === null || cellValue === '') return null;

  // Check for placeholder values
  if (detectPlaceholder(cellValue)) {
    return null;
  }

  const str = String(cellValue).toLowerCase().trim();

  // Already a number
  if (typeof cellValue === 'number') {
    // If > 1, assume cents not dollars
    return cellValue > 1 ? cellValue / 100 : cellValue;
  }

  // Strip currency symbols and try direct parse
  const cleanedRate = str.replace(/[$€£¥,\s]/g, '');
  const directValue = parseFloat(cleanedRate);
  if (!isNaN(directValue)) {
    return directValue > 1 ? directValue / 100 : directValue;
  }

  // Handle "X-Cents", "X cents", or "X¢" format (with actual cent symbol)
  const centsMatch = str.match(/(\d+(?:\.\d+)?)\s*[-]?\s*(cents?|¢)/i);
  if (centsMatch) {
    return parseFloat(centsMatch[1]) / 100;
  }

  return null;
}

/**
 * Parse tonnage from various formats
 * Handles: "36", "3x12=36", "36 tons", "1.5/2" (range), "57 (for apartments)", formulas
 *
 * @param {any} cellValue - Raw cell value
 * @param {number} rowNum - Row number for logging
 * @returns {{ value: number|null, isPlaceholder: boolean }} Parsed tonnage with metadata
 */
function parseTonnage(cellValue, rowNum = 0) {
  if (cellValue === undefined || cellValue === null || cellValue === '') {
    return { value: null, isPlaceholder: false };
  }

  // Check for placeholder values (X, TBD, N/A, etc.)
  if (detectPlaceholder(cellValue)) {
    console.log(`[EAS Parser] Row ${rowNum}: Tonnage placeholder detected: "${cellValue}"`);
    return { value: null, isPlaceholder: true };
  }

  // Already a number
  if (typeof cellValue === 'number') {
    return { value: cellValue > 0 ? cellValue : null, isPlaceholder: false };
  }

  const str = String(cellValue).trim();

  // Handle "NxM=R" format (e.g., "3x12=36") - take the result after =
  const formulaMatch = str.match(/=\s*(\d+(?:\.\d+)?)/);
  if (formulaMatch) {
    return { value: parseFloat(formulaMatch[1]), isPlaceholder: false };
  }

  // Handle "NxM" format without result - calculate it
  const multiplyMatch = str.match(/^(\d+(?:\.\d+)?)\s*[xX*]\s*(\d+(?:\.\d+)?)$/);
  if (multiplyMatch) {
    return { value: parseFloat(multiplyMatch[1]) * parseFloat(multiplyMatch[2]), isPlaceholder: false };
  }

  // Handle range notation (1.5/2 or 3-5) - take average
  const rangeMatch = str.match(/^(\d+(?:\.\d+)?)\s*[\/]\s*(\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    const avg = (parseFloat(rangeMatch[1]) + parseFloat(rangeMatch[2])) / 2;
    console.log(`[EAS Parser] Row ${rowNum}: Tonnage range "${str}" → using average: ${avg}`);
    return { value: avg, isPlaceholder: false };
  }

  // Handle parenthetical notes: "57 (for apartments)" or "3(3x7=21)" - extract leading number
  const noteMatch = str.match(/^(\d+(?:\.\d+)?)\s*\(/);
  if (noteMatch) {
    return { value: parseFloat(noteMatch[1]), isPlaceholder: false };
  }

  // Strip commas and "tons" suffix
  const cleaned = str.replace(/,/g, '').replace(/\s*tons?\s*$/i, '').trim();
  const numValue = parseFloat(cleaned);
  return { value: isNaN(numValue) || numValue <= 0 ? null : numValue, isPlaceholder: false };
}

/**
 * Check if a row has meaningful equipment data
 * Filters out rows that only have a row number or are effectively empty
 * Also filters out summary/totals rows that have data but no equipment type
 *
 * @param {Array} row - Row data
 * @param {Object} columnMap - Column mapping
 * @returns {boolean} True if row has equipment data
 */
function hasEquipmentData(row, columnMap) {
  if (!row) return false;

  // Find relevant column indices
  let hasEquipmentTypeHint = false;
  let hasRequiredData = false;
  let dataColumnCount = 0;

  for (const [colIdx, fieldName] of Object.entries(columnMap)) {
    const value = row[parseInt(colIdx, 10)];
    if (value === undefined || value === null || value === '') continue;

    // Skip row number columns
    if (fieldName === 'equipment_id') continue;

    dataColumnCount++;

    // Check if we have equipment type indicators
    if (fieldName === 'equipment_type' || fieldName === 'rtu_or_split') {
      const normalized = normalizeEquipmentType(value);
      if (normalized !== 'Unknown') hasEquipmentTypeHint = true;
    }

    // Check location for equipment type hints
    if (fieldName === 'location') {
      const normalized = normalizeEquipmentType(value);
      if (normalized !== 'Unknown') hasEquipmentTypeHint = true;
    }

    // Check for essential data fields
    if (['year_manufactured', 'operating_hours', 'manufacturer'].includes(fieldName)) {
      hasRequiredData = true;
    }
  }

  // Valid row must have:
  // 1. At least 2 data columns AND
  // 2. Either an equipment type hint OR essential data like manufacturer + year
  return dataColumnCount >= 2 && (hasEquipmentTypeHint || (hasRequiredData && dataColumnCount >= 4));
}

/**
 * Parse a single data row into equipment object
 *
 * @param {Array} row - Array of cell values
 * @param {Object} columnMap - Map of column index to field name
 * @param {number} rowNum - Row number (for validation)
 * @returns {Object} Parsed equipment object
 */
function parseEquipmentRow(row, columnMap, rowNum) {
  const equipment = {
    row_number: rowNum,
    equipment_id: null,
    equipment_type: null,
    rtu_or_split: null,  // Secondary type column
    location: null,
    units: 1,  // Default to 1 if not specified
    manufacturer: null,
    model: null,
    year_manufactured: null,
    age: null,
    tonnage: null,
    refrigerant: null,
    operating_hours: null,
    electricity_rate: null,
    hasWarnings: false,
    warnings: []
  };

  // Extract values based on column map
  Object.entries(columnMap).forEach(([colIdx, fieldName]) => {
    const cellValue = row[parseInt(colIdx, 10)];

    if (cellValue === undefined || cellValue === null || cellValue === '') {
      return;  // Skip empty cells
    }

    // Field-specific parsing
    switch (fieldName) {
      case 'equipment_type':
        equipment.equipment_type = normalizeEquipmentType(cellValue);
        break;

      case 'rtu_or_split':
        // Store for later processing
        equipment.rtu_or_split = normalizeEquipmentType(cellValue);
        break;

      case 'electricity_rate':
        equipment.electricity_rate = parseElectricityRate(cellValue);
        break;

      case 'tonnage':
        const tonnageResult = parseTonnage(cellValue, rowNum);
        equipment.tonnage = Math.ceil(tonnageResult.value);
        equipment.tonnageIsPlaceholder = tonnageResult.isPlaceholder;
        break;

      case 'operating_hours':
        // Check for placeholder values
        if (detectPlaceholder(cellValue)) {
          console.log(`[EAS Parser] Row ${rowNum}: Operating hours placeholder detected: "${cellValue}"`);
          equipment.operating_hours = null;
          equipment.warnings.push(`Operating hours shows placeholder value "${cellValue}"`);
          equipment.hasWarnings = true;
          break;
        }
        // Detect non-evaluable formulas with cell references
        if (typeof cellValue === 'string' && cellValue.startsWith('=') && /[A-Z]+\d+/.test(cellValue)) {
          console.warn(`[EAS Parser] Row ${rowNum}: Formula with cell reference "${cellValue}" - value may not be calculated correctly`);
          equipment.warnings.push(`Operating hours contains formula with cell reference - verify value`);
          equipment.hasWarnings = true;
        }
        // Strip commas and spaces from numeric values
        const cleanedHours = String(cellValue).replace(/[,\s]/g, '');
        const hoursValue = parseFloat(cleanedHours);
        equipment.operating_hours = isNaN(hoursValue) ? cellValue : hoursValue;
        break;

      case 'year_manufactured':
        // Handle various year formats: "2019", "Jan-95", "1995", "95", "January 1995", "Q1 2020", "FY2020"
        const yearStr = String(cellValue).trim();
        let yearValue = parseInt(yearStr, 10);

        if (isNaN(yearValue)) {
          // Try parsing "Mon-YY" format (e.g., "Jan-95")
          const dateMatch = yearStr.match(/^[a-zA-Z]{3}-(\d{2})$/);
          if (dateMatch) {
            const twoDigitYear = parseInt(dateMatch[1], 10);
            // 00-30 → 2000s (modern equipment), 31-99 → 1900s
            yearValue = twoDigitYear <= 30 ? 2000 + twoDigitYear : 1900 + twoDigitYear;
          }

          // Try parsing full month format: "January 1995"
          const fullMonthMatch = yearStr.match(/^(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})$/i);
          if (fullMonthMatch) {
            yearValue = parseInt(fullMonthMatch[2], 10);
          }

          // Try parsing quarter format: "Q1 2020"
          const quarterMatch = yearStr.match(/^Q[1-4]\s*(\d{4})$/i);
          if (quarterMatch) {
            yearValue = parseInt(quarterMatch[1], 10);
          }

          // Try parsing fiscal year: "FY2020" or "FY20"
          const fyMatch = yearStr.match(/^FY\s*(\d{2,4})$/i);
          if (fyMatch) {
            const fyYear = parseInt(fyMatch[1], 10);
            yearValue = fyYear < 100 ? (fyYear <= 30 ? 2000 + fyYear : 1900 + fyYear) : fyYear;
          }
        } else if (yearValue < 100) {
          // Two-digit year: 00-30 → 2000s (modern equipment), 31-99 → 1900s
          yearValue = yearValue <= 30 ? 2000 + yearValue : 1900 + yearValue;
        }

        equipment.year_manufactured = isNaN(yearValue) ? cellValue : yearValue;
        break;

      case 'units':
        const unitsNum = parseInt(cellValue, 10);
        equipment.units = isNaN(unitsNum) ? 1 : Math.max(1, unitsNum);
        break;

      default:
        equipment[fieldName] = String(cellValue).trim();
    }
  });

  // Resolve equipment type from multiple sources
  // Priority: rtu_or_split > equipment_type > location
  if (equipment.rtu_or_split && equipment.rtu_or_split !== 'Unknown') {
    // If rtu_or_split has a valid type (RTU, Split), use it
    equipment.equipment_type = equipment.rtu_or_split;
  } else if (!equipment.equipment_type || equipment.equipment_type === 'Unknown' || equipment.equipment_type === 'Split') {
    // If equipment_type is missing/unknown/DX, check location for type hints
    if (equipment.location) {
      const locationNormalized = normalizeEquipmentType(equipment.location);
      if (locationNormalized !== 'Unknown') {
        equipment.equipment_type = locationNormalized;
      }
    }
  }

  // Calculate age if year_manufactured present
  if (equipment.year_manufactured && typeof equipment.year_manufactured === 'number') {
    equipment.age = calculateAge(equipment.year_manufactured);
  }

  // If year/age missing, estimate as 10 years old (reasonable middle value)
  if (equipment.age === null || equipment.age === undefined || isNaN(equipment.age)) {
    equipment.age = 10;
    equipment.ageEstimated = true;
    equipment.warnings.push('Year not specified, using estimated age: 10 years');
    equipment.hasWarnings = true;
    console.log(`[EAS Parser] Row ${rowNum}: Age estimated as 10 years (year not specified)`);
  }

  // If tonnage missing, estimate from equipment type
  if (!equipment.tonnage || isNaN(equipment.tonnage) || equipment.tonnage <= 0) {
    const estimatedTonnage = DEFAULT_TONNAGE[equipment.equipment_type] || DEFAULT_TONNAGE['Unknown'];
    equipment.tonnage = estimatedTonnage;
    equipment.tonnageEstimated = true;

    // Provide more specific warning for placeholder values
    if (equipment.tonnageIsPlaceholder) {
      equipment.warnings.push(`Tonnage shows placeholder value, using estimate: ${estimatedTonnage} tons`);
    } else {
      equipment.warnings.push(`Tonnage not specified, using estimate: ${estimatedTonnage} tons`);
    }
    equipment.hasWarnings = true;
    console.log(`[EAS Parser] Row ${rowNum}: Tonnage estimated as ${estimatedTonnage} tons for ${equipment.equipment_type}`);
  }

  return equipment;
}

/**
 * Parse EAS Excel file (client-side)
 *
 * @param {File} file - User-uploaded Excel file
 * @returns {Promise<Object>} Parsed data with metadata
 */
export async function parseEASFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        console.log(`[EAS Parser] ========== Starting file parse: ${file.name} ==========`);

        // Read Excel file
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        console.log(`[EAS Parser] Sheets found: ${workbook.SheetNames.join(', ')}`);
        console.log(`[EAS Parser] Using sheet: "${sheetName}"`);

        // Convert to array of arrays
        const rows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,  // Return array of arrays
          raw: false,  // Don't return raw values (evaluate formulas)
          defval: null  // Use null for empty cells
        });
        console.log(`[EAS Parser] Total rows in sheet: ${rows.length}`);

        // Find header row
        const headerRowIdx = findHeaderRow(rows);
        if (headerRowIdx === -1) {
          const errorMsg = 'Could not find header row in Excel file. Expected columns: Equipment Type, Tonnage, Year Manufactured, Operating Hours, Electricity Rate';
          console.error(`[EAS Parser] ✗ PARSE FAILED: ${errorMsg}`);
          return reject(new Error(errorMsg));
        }

        // Map columns
        const headerRow = rows[headerRowIdx];
        const columnMap = mapColumns(headerRow);

        // Check if required columns present
        const mappedFields = Object.values(columnMap);
        const missingRequired = REQUIRED_FIELDS.filter(field => !mappedFields.includes(field));
        if (missingRequired.length > 0) {
          const foundFields = mappedFields.filter(f => REQUIRED_FIELDS.includes(f));
          const errorMsg = `Missing required columns: ${missingRequired.join(', ')}. Found: ${foundFields.join(', ') || 'none'}`;
          console.error(`[EAS Parser] ✗ PARSE FAILED: ${errorMsg}`);
          return reject(new Error(errorMsg));
        }

        // Parse data rows (skip header and any rows before it)
        const dataRows = rows.slice(headerRowIdx + 1);
        const equipment = [];

        dataRows.forEach((row, idx) => {
          // Skip empty rows
          if (!row || row.every(cell => cell === null || cell === '')) {
            return;
          }

          // Skip rows that don't have meaningful equipment data
          // (e.g., rows with just a row number but no other data)
          if (!hasEquipmentData(row, columnMap)) {
            return;
          }

          const parsed = parseEquipmentRow(row, columnMap, idx + 1);

          // Skip rows where we couldn't determine equipment type
          // (these are likely summary rows or headers repeated)
          if (!parsed.equipment_type || parsed.equipment_type === 'Unknown') {
            console.log(`[EAS Parser] Row ${idx + 1}: Skipped - no valid equipment type`);
            return;
          }

          equipment.push(parsed);
        });

        // Extract metadata
        const metadata = {
          filename: file.name,
          uploadDate: new Date().toISOString(),
          totalEquipment: equipment.length,
          sheetName: sheetName,
          headerRow: headerRowIdx + 1  // 1-indexed for display
        };

        // Log success summary
        console.log(`[EAS Parser] ✓ Parse successful!`);
        console.log(`[EAS Parser]   Equipment rows: ${equipment.length}`);
        console.log(`[EAS Parser]   Equipment types: ${[...new Set(equipment.map(e => e.equipment_type))].join(', ')}`);
        console.log(`[EAS Parser] ========== End parse ==========`);

        resolve({
          metadata,
          equipment,
          validation: {
            valid: true,  // Initial validation (detailed validation happens separately)
            errors: [],
            warnings: []
          }
        });
      } catch (error) {
        console.error(`[EAS Parser] ✗ PARSE ERROR: ${error.message}`);
        console.error(error.stack);
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

// ============================================================================
// DEFAULT EXPORT - All public APIs
// ============================================================================

export default {
  // Constants
  EAS_COLUMN_PATTERNS,
  EQUIPMENT_TYPE_MAP,
  REQUIRED_FIELDS,
  OPTIONAL_FIELDS,
  DEFAULT_TONNAGE,

  // Functions
  parseEASFile,
  validateEAS,
  validateEquipment,
  normalizeEquipmentType,
  calculateAge
};
